import { useState, useEffect, useCallback } from 'react';

const PINNED_CHANNELS_KEY = 'pinnedChannels';
const PINNED_ORDER_KEY = 'pinnedChannelsOrder';

// Custom event for cross-component sync
const PINS_UPDATED_EVENT = 'pinnedChannelsUpdated';

export function usePinnedChannels() {
  const [pinnedChannels, setPinnedChannelsState] = useState<Set<string>>(() => {
    const stored = localStorage.getItem(PINNED_CHANNELS_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  const [pinnedOrder, setPinnedOrderState] = useState<string[]>(() => {
    const stored = localStorage.getItem(PINNED_ORDER_KEY);
    const storedPins = localStorage.getItem(PINNED_CHANNELS_KEY);
    const order = stored ? JSON.parse(stored) : [];
    const pins = storedPins ? JSON.parse(storedPins) : [];
    
    // Sync: add any pinned channels that aren't in the order
    const orderSet = new Set(order);
    const missingPins = pins.filter((id: string) => !orderSet.has(id));
    if (missingPins.length > 0) {
      const newOrder = [...order, ...missingPins];
      localStorage.setItem(PINNED_ORDER_KEY, JSON.stringify(newOrder));
      return newOrder;
    }
    return order;
  });

  // Listen for updates from other components
  useEffect(() => {
    const handleUpdate = () => {
      const storedPins = localStorage.getItem(PINNED_CHANNELS_KEY);
      const storedOrder = localStorage.getItem(PINNED_ORDER_KEY);
      
      if (storedPins) {
        setPinnedChannelsState(new Set(JSON.parse(storedPins)));
      }
      if (storedOrder) {
        setPinnedOrderState(JSON.parse(storedOrder));
      }
    };

    window.addEventListener(PINS_UPDATED_EVENT, handleUpdate);
    window.addEventListener('storage', handleUpdate);
    
    return () => {
      window.removeEventListener(PINS_UPDATED_EVENT, handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, []);

  const setPinnedChannels = useCallback((updater: (prev: Set<string>) => Set<string>) => {
    setPinnedChannelsState(prev => {
      const next = updater(prev);
      localStorage.setItem(PINNED_CHANNELS_KEY, JSON.stringify([...next]));
      window.dispatchEvent(new CustomEvent(PINS_UPDATED_EVENT));
      return next;
    });
  }, []);

  const setPinnedOrder = useCallback((updater: (prev: string[]) => string[]) => {
    setPinnedOrderState(prev => {
      const next = updater(prev);
      localStorage.setItem(PINNED_ORDER_KEY, JSON.stringify(next));
      window.dispatchEvent(new CustomEvent(PINS_UPDATED_EVENT));
      return next;
    });
  }, []);

  const togglePin = useCallback((channelId: string) => {
    setPinnedChannels(prev => {
      const next = new Set(prev);
      if (next.has(channelId)) {
        next.delete(channelId);
        // Remove from order
        setPinnedOrder(order => order.filter(id => id !== channelId));
      } else {
        next.add(channelId);
        // Add to end of order
        setPinnedOrder(order => [...order, channelId]);
      }
      return next;
    });
  }, [setPinnedChannels, setPinnedOrder]);

  const reorderPins = useCallback((draggedId: string, targetId: string) => {
    setPinnedOrder(order => {
      const newOrder = [...order];
      const draggedIndex = newOrder.indexOf(draggedId);
      const targetIndex = newOrder.indexOf(targetId);

      if (draggedIndex !== -1 && targetIndex !== -1) {
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(targetIndex, 0, draggedId);
        return newOrder;
      }
      
      return order;
    });
  }, [setPinnedOrder]);

  return {
    pinnedChannels,
    pinnedOrder,
    togglePin,
    reorderPins,
  };
}
